import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import * as Redux from 'redux';
import { batch, connect } from 'react-redux';
import { push } from 'connected-react-router';
import isEqual from 'lodash/isEqual';
import { ROUTE_NAME, ROUTE_PATH } from 'utils/constants/router';
import Spinner from 'components/Spinner';
import {
  setIsFetching,
  initQuestionnaire,
  skipStep,
  backStep,
  nextStep,
  goToQuestion,
  setIsNotLastQuestion,
  setSkipStep,
  setQuestionnaireInitialized,
} from 'components/Questionnaire/actions';
import withUserLeavingApp from 'utils/hocs/withUserLeavingApp';
import { authenticationActions, setIsNewProfile } from 'components/Authentication/actions';
import app from 'new-ui/app';
import { GA_SIGNUP_CATEGORY, GA_SIGNUP } from 'modules/ga/generalEvents';
import
questionAnsweredEvents, {
  GA_CATEGORY_NAME as GA_QUESTION_ANSWERED_CATEGORY_NAME,
} from 'modules/ga/questionAnsweredEvents';
import questionUpdateEvents, {
  GA_CATEGORY_NAME as GA_QUESTION_UPDATED_CATEGORY_NAME,
} from 'modules/ga/questionUpdateEvents';
import { checkIsAuthenticated } from 'functions/checkIsAuthenticated';
import { confirmBeforeLeave } from './functions/confirmBeforeLeave';
import { checkQuestionAnswer } from './functions/checkQuestionAnswer';
import { reformatOptionsFromServer } from './actions';
import { QuestionnaireProvider } from './context';
import { getQuestionIdByIndex, findIndexOfQuestionInQstack } from './helpers';
import { QuestionnaireView } from './QuestionnaireView';

// it is expected the component to be rendered only isAuthPending is false
class Questionnaire extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      shouldChangePrevBtnEvent: false,
      shouldChangeSkipBtnEvent: false,
    };
    this.questionRef = React.createRef();
    this.gaSentAnsweredQuestion = null;
    this.gaSentUpdatedQuestion = null;
  }

  componentDidMount() {
    const {
      profile, dispatch, isAuthenticated, i18n, question: { id: questionId }, targetQuestionNotFoundAction,
    } = this.props;

    if (!isAuthenticated) {
      window.addEventListener('beforeunload', confirmBeforeLeave);
    }

    i18n.on('languageChanged', this.onLanguageChange);
    (async () => {
      try {
        const getIsRequiredPersonalDataPresent = ({ personal: { patient_id, profile_id, condition } } = {}) => (
          !!(patient_id && profile_id && condition)
        );

        let profileData;
        if (!isAuthenticated) {
          dispatch(setIsNewProfile(true));
          profileData = profile;
        } else {
          profileData = profile;
          if (!getIsRequiredPersonalDataPresent(profile)) {
            // should be invariant
            profileData = await dispatch(authenticationActions.getUser());
          }
        }

        const isRequiredPersonalDataPresent = getIsRequiredPersonalDataPresent(profileData);

        const { personal: { condition } = {} } = profileData;

        dispatch(setIsNotLastQuestion);
        if (!isRequiredPersonalDataPresent) {
          this.props.setIsFetching(true);

          if (isAuthenticated) {
            // invariant
            batch(() => {
              dispatch(push(ROUTE_PATH[ROUTE_NAME.RESULTS]));
              dispatch(setIsFetching(false));
            });
          } else {
            dispatch(initQuestionnaire({ condition, questionId, targetQuestionNotFoundAction }));
          }
        } else {
          dispatch(initQuestionnaire({ condition, questionId, targetQuestionNotFoundAction }));
        }

        document.addEventListener('keydown', this.shortCutListener);
      } catch (ex) {
        console.error(`Error occurred, while updating user profile: ${(ex && ex.message) || ex}`);
      }
    })();
  }

  componentDidUpdate(prevProps) {
    const {
      questionnaire,
      userGet,
      profile,
      question,
      targetQuestionNotFoundAction,
      isAuthenticated,
    } = this.props;
    const {
      user_wix_id: thisUserWixId,
      patient_id: thisPatientId,
      profile_id: thisProfileId,
      condition: thisCondition,
    } = profile.personal;

    const {
      questionnaire: {
        form: prevForm,
        isInitialized: prevIsInitialized,
      } = {},
      question: prevQuestion,
    } = prevProps;
    const {
      currentIndex, qstack, form, isInitialized,
    } = questionnaire;
    const currentQuestionId = getQuestionIdByIndex(currentIndex, qstack);

    if (
      thisUserWixId
      && thisPatientId
      && thisProfileId
      && thisCondition
      && !userGet.isFetching
      && questionnaire.isFetching
      && !questionnaire.isFetchingOptionsFromServer
    ) {
      this.props.setIsFetching(false);
      this.props.dispatch(initQuestionnaire({
        condition: thisCondition,
        questionId: currentQuestionId,
        targetQuestionNotFoundAction,
      }));
    }

    const questionPrevValue = prevForm[currentQuestionId];
    const questionCurrentValue = form[currentQuestionId];

    if (!prevIsInitialized && isInitialized && currentIndex === 0 && !isAuthenticated) {
      // the first question should be shown
      app.sendGoogleAnalyticsEvent(GA_SIGNUP_CATEGORY, GA_SIGNUP.QUESTIONNAIRE_FIRST_QUESTION_VIEWED);
    }

    if (
      currentQuestionId
      && currentQuestionId !== this.gaSentAnsweredQuestion
      && thisCondition
      && questionCurrentValue
    ) {
      this.gaSentAnsweredQuestion = currentQuestionId;

      const { eventName, getMetaData } = questionAnsweredEvents.questionAnswered;
      app.sendGoogleAnalyticsEvent(GA_QUESTION_ANSWERED_CATEGORY_NAME, eventName, getMetaData({
        cancerType: thisCondition,
      }));
    }

    if (
      currentQuestionId
      && currentQuestionId !== this.gaSentUpdatedQuestion
      && thisCondition
      && questionPrevValue
      && questionCurrentValue
      && !isEqual(questionPrevValue, questionCurrentValue)
    ) {
      this.gaSentUpdatedQuestion = currentQuestionId;

      const { eventName, getMetaData } = questionUpdateEvents.answerUpdated;
      app.sendGoogleAnalyticsEvent(GA_QUESTION_UPDATED_CATEGORY_NAME, eventName, getMetaData({
        cancerType: thisCondition,
      }));
    }

    if (question !== prevQuestion || !prevQuestion) {
      const targetQuestionCurrentIndex = findIndexOfQuestionInQstack(question.id, qstack);

      if (targetQuestionCurrentIndex === -1 && targetQuestionNotFoundAction) {
        this.props.dispatch(targetQuestionNotFoundAction(question.id));
      }

      if (targetQuestionCurrentIndex >= 0 && targetQuestionCurrentIndex !== currentIndex) {
        this.props.goToQuestion({
          currentIndex: targetQuestionCurrentIndex,
        });
      }
    }
  }

  componentWillUnmount() {
    const { i18n } = this.props;
    i18n.off('languageChanged', this.onLanguageChange);
    document.removeEventListener('keydown', this.shortCutListener);

    this.props.dispatch(setQuestionnaireInitialized(false));
  }

  onLanguageChange = () => {
    const { dispatch } = this.props;
    dispatch(reformatOptionsFromServer);
  };

  shortCutListener = (event) => {
    if (this.props.isUserUpdating || this.props.embedded) {
      return;
    }

    const { beforeNextStepAction, changeStepAction } = this.props;

    if (event.key === 'ArrowRight') {
      this.props.skipStep({ beforeNextStepAction, changeStepAction });
      this.props.setSkipStep();
    } else if (event.key === 'ArrowLeft') {
      this.props.backStep({ changeStepAction });
    }
  };

  handleSubmit = (event) => {
    event.preventDefault();
    const { beforeNextStepAction, changeStepAction } = this.props;
    this.props.nextStep({ beforeNextStepAction, changeStepAction });
  };

  handlePreviousButton = () => {
    if (this.state.shouldChangePrevBtnEvent) {
      this.questionRef.current.prevBtnEvent();
    } else {
      const { changeStepAction } = this.props;
      this.props.backStep({ changeStepAction });
    }
  };

  handleSkipButton = () => {
    if (this.state.shouldChangeSkipBtnEvent) {
      // no one question implements skipBtnEvent for now
      this.questionRef.current.skipBtnEvent();
    } else {
      const { beforeNextStepAction, changeStepAction } = this.props;
      this.props.skipStep({ beforeNextStepAction, changeStepAction });
    }
  };

  setAbilityToChangeSkipBtnEvent = (state) => {
    this.setState({
      shouldChangeSkipBtnEvent: state,
    });
  };

  setAbilityToChangePrevBtnEvent = (state) => {
    this.setState({
      shouldChangePrevBtnEvent: state,
    });
  };

  render() {
    const {
      isUserUpdating,
      questionnaire,
      profile,
      geoLocation,
      provider,
      embedded,
      beforeNextStepAction,
      changeStepAction,
    } = this.props;
    const {
      isInitialized,
      currentIndex,
      qstack,
      form,
      files,
      isLast,
    } = questionnaire;

    if (
      isLast
      || !isInitialized
      || !qstack?.length
      || !(typeof currentIndex === 'number' && currentIndex >= 0)
    ) {
      return embedded ? null : <Spinner />;
    }

    const currentQuestion = qstack[currentIndex];

    const isCurrentQuestionAnswered = checkQuestionAnswer(currentQuestion, { form, files, geoLocation });

    return (
      <QuestionnaireProvider value={{ embedded, beforeNextStepAction, changeStepAction }}>
        <QuestionnaireView
          questionRef={this.questionRef}
          isCurrentQuestionAnswered={isCurrentQuestionAnswered}
          provider={provider}
          embedded={embedded}
          handleSubmit={this.handleSubmit}
          handleSkipButton={this.handleSkipButton}
          handlePreviousButton={this.handlePreviousButton}
          currentQuestion={currentQuestion}
          isUserUpdating={isUserUpdating}
          isLast={isLast}
          profile={profile}
          isPreviousButtonDisable={currentIndex <= 0}
          setAbilityToChangePrevBtnEvent={this.setAbilityToChangePrevBtnEvent}
          setAbilityToChangeSkipBtnEvent={this.setAbilityToChangeSkipBtnEvent}
        />
      </QuestionnaireProvider>
    );
  }
}

Questionnaire.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  questionnaire: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  userGet: PropTypes.object.isRequired,
  isUserUpdating: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  geoLocation: PropTypes.object.isRequired,
  skipStep: PropTypes.func.isRequired,
  backStep: PropTypes.func.isRequired,
  goToQuestion: PropTypes.func.isRequired,
  setIsFetching: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  provider: PropTypes.any,
  setSkipStep: PropTypes.func.isRequired,
  question: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  embedded: PropTypes.bool,
  beforeNextStepAction: PropTypes.func,
  changeStepAction: PropTypes.func,
  targetQuestionNotFoundAction: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isAuthenticated: checkIsAuthenticated({ authentication: state.authentication, profile: state.profile.data }),
  questionnaire: state.questionnaire,
  profile: state.profile.data,
  userGet: state.userGet,
  isUserUpdating: (state.userUpdate && state.userUpdate.isFetching) || (state.matches && state.matches.isFetching)
  || (state.questionnaire && state.questionnaire.isFetching),
  geoLocation: state.geo_location,
  form: state.questionnaire.form,
  provider: state?.appConfiguration?.providerSpecific?.code,
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  setIsFetching: (payload) => dispatch(setIsFetching(payload)),
  setSkipStep: (payload) => dispatch(setSkipStep(payload)),
  skipStep: (payload) => dispatch(skipStep(payload)),
  backStep: (payload) => dispatch(backStep(payload)),
  nextStep: (payload) => dispatch(nextStep(payload)),
  goToQuestion: (payload) => dispatch(goToQuestion(payload)),
});

export default Redux.compose(
  connect(mapStateToProps, mapDispatchToProps),
  withUserLeavingApp,
  withTranslation(),
)(Questionnaire);
